<template>
  <div class="landing col__white">
    <LandingBanner :endTime="countDown" :afterSale="afterSale" id="banner" />
    <DiscountBar />

    <!-- ProductSection Component Test -->
    <ProductSection
      :productData="sampleProductData"
      @color-changed="handleColorChanged"
      @add-to-cart="handleAddToCart"
      id="product-section"
    />

    <!-- AddonSection Component -->
    <AddonSection id="addon-section" />

    <PurchaseProcessSection />

    <div class="crossSelling-section">
      <!-- Tab Selector Component -->
      <TabSelector :selectedTab="activeTab" @tab-changed="handleTabChanged" />
      <!-- CrossSelling Component -->
      <CrossSelling
        :carouselProducts="carouselProducts1"
        v-show="activeTab === 'vertuo'"
        key="vertuo"
      />
      <!-- CrossSelling Component -->
      <CrossSelling
        :carouselProducts="carouselProducts2"
        v-show="activeTab === 'original'"
        key="original"
      />
    </div>

    <!-- PromoteSection Component -->
    <PromoteSection />

    <MegaOfferPopup
      :isVisible="isMegaPopupOpen"
      @close="isMegaPopupOpen = false"
    />

    <a @click="isMegaPopupOpen = true" class="floating--but">
      <div class="floating--but__content">
        <ImageWrapper
          src="/assets/images/2025/mega_sticky.png"
          class="mega"
          alt=""
        />
      </div>
      <span class="floating--but__text">{{ $t("megaOffer") }}</span>
    </a>
  </div>
</template>

<script>
import dayjs from "dayjs";
import ProductShowcasePage from "./ProductShowcasePage.vue";
import counter from "../atoms/counter.vue";
import DiscountBar from "../molecules/DiscountBar.vue";
import TitleSection from "../molecules/TitleSection.vue";
import TabSelector from "../molecules/TabSelector.vue";
import ProductSection from "../organisms/ProductSection.vue";
import PromoteSection from "../organisms/PromoteSection.vue";
import CrossSelling from "../organisms/CrossSelling.vue";
import AddonSection from "../organisms/AddonSection.vue";
import PurchaseProcessSection from "../organisms/PurchaseProcessSection.vue";
import LandingBanner from "../organisms/LandingBanner.vue";
import MegaOfferPopup from "../organisms/MegaOfferPopup.vue";
import ImageWrapper from "../atoms/ImageWrapper.vue";

const SALE_DATE = "2025-11-05";
const SALE_DATE_END = "2025-11-12 23:59:59";

export default {
  name: "Landing",

  /**
   * Component data initialization
   * Determines countdown target, stage, and sale status based on current date
   * Can be overridden by URL parameter ?stage=xxxx
   * @returns {Object} Component data object
   */
  data() {
    const currentDate = dayjs();
    const saleDate = dayjs(SALE_DATE);
    const saleEndDate = dayjs(SALE_DATE_END);

    // Check for stage override from URL parameters
    const urlStageOverride = this.getStageFromUrl();

    console.log("urlStageOverride: ", urlStageOverride);

    // Determine stage and related properties based on current date or URL override
    const stageData = urlStageOverride
      ? this.calculateStageDataWithOverride(
          urlStageOverride,
          saleDate,
          saleEndDate,
        )
      : this.calculateStageData(currentDate, saleDate, saleEndDate);

    return {
      isMegaPopupOpen: false,
      /**
       * Current sale stage: 'beforeSale', 'inSale', or 'afterSale'
       */
      stage: stageData.stage,
      /**
       * Countdown timestamp calculated based on current date and stage
       * If beforeSale: targets SALE_DATE
       * If inSale: targets SALE_DATE_END
       * If afterSale: -1 (no countdown)
       */
      countDown: stageData.countDown,
      /**
       * Boolean indicating if currently in sale period
       */
      isInSale: stageData.isInSale,
      /**
       * Boolean indicating if sale period has ended
       */
      afterSale: stageData.afterSale,
      activeTab: "vertuo", // Default active tab
      selectedCrossSelling: "carouselProducts2",
      sampleProductData: [
        {
          name: "AEROCCINO3 BUNDLE",
          desc: ["Essenza Mini", "Spoon, set of 6", "Barista towels, set of 2"],
          price: "499",
          orgPrice: "1692",
          discount: "47% OFF",
          img: "https://s3.ap-southeast-1.amazonaws.com/nespresso.web/2025double11/assets/images/milk_lover.png",
          name: "Piano Black",
          colorCode: "#000000",
          sku: "SKUXX1",
        },
      ],
    };
  },

  provide() {
    return {
      stage: this.stage,
    };
  },

  components: {
    ProductShowcasePage,
    counter,
    DiscountBar,
    TitleSection,
    TabSelector,
    ProductSection,
    PromoteSection,
    CrossSelling,
    AddonSection,
    PurchaseProcessSection,
    LandingBanner,
    MegaOfferPopup,
    ImageWrapper,
  },

  computed: {
    /**
     * Returns the products for the carousel (excluding the first product)
     * @returns {Array} Array of product objects for carousel display
     */
    carouselProducts1() {
      const products = this.$t("crossSelling.products1");
      return products; // Remove first product as it's displayed separately
    },
    carouselProducts2() {
      const products = this.$t("crossSelling.products2");
      return products; // Remove first product as it's displayed separately
    },
  },

  watch: {
    /**
     * Watches for activeTab changes and dispatches a window resize event
     * This helps components that depend on window size to recalculate their dimensions
     * @param {String} newTab - The new active tab value
     * @param {String} oldTab - The previous active tab value
     */
    activeTab(newTab, oldTab) {
      // Dispatch resize event to trigger layout recalculations
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    },
  },

  methods: {
    /**
     * Extracts stage parameter from URL query string
     * @returns {String|null} Stage value from URL parameter or null if not present
     */
    getStageFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const stageParam = urlParams.get("stage");

      // Validate stage parameter against allowed values
      const allowedStages = ["beforeSale", "inSale", "afterSale"];
      if (stageParam && allowedStages.includes(stageParam)) {
        return stageParam;
      }

      return null;
    },

    /**
     * Calculates stage data with URL override
     * @param {String} overrideStage - Stage override from URL parameter
     * @param {Object} saleDate - Sale start date (dayjs object)
     * @param {Object} saleEndDate - Sale end date (dayjs object)
     * @returns {Object} Stage data object with stage, countDown, isInSale, and afterSale properties
     */
    calculateStageDataWithOverride(overrideStage, saleDate, saleEndDate) {
      let countDown;
      let isInSale;
      let afterSale;

      switch (overrideStage) {
        case "beforeSale":
          countDown = saleDate.valueOf();
          isInSale = false;
          afterSale = false;
          break;
        case "inSale":
          countDown = saleEndDate.valueOf();
          isInSale = true;
          afterSale = false;
          break;
        case "afterSale":
          countDown = -1;
          isInSale = false;
          afterSale = true;
          break;
        default:
          // Fallback to current date calculation if invalid stage
          const currentDate = dayjs();
          return this.calculateStageData(currentDate, saleDate, saleEndDate);
      }

      return {
        stage: overrideStage,
        countDown,
        isInSale,
        afterSale,
      };
    },

    /**
     * Calculates stage data based on current date and sale dates
     * @param {Object} currentDate - Current date (dayjs object)
     * @param {Object} saleDate - Sale start date (dayjs object)
     * @param {Object} saleEndDate - Sale end date (dayjs object)
     * @returns {Object} Stage data object with stage, countDown, isInSale, and afterSale properties
     */
    calculateStageData(currentDate, saleDate, saleEndDate) {
      let stage;
      let countDown;
      let isInSale;
      let afterSale;

      if (currentDate.isBefore(saleDate)) {
        // Before sale period
        stage = "beforeSale";
        countDown = saleDate.valueOf();
        isInSale = false;
        afterSale = false;
      } else if (currentDate.isBefore(saleEndDate)) {
        // During sale period
        stage = "inSale";
        countDown = saleEndDate.valueOf();
        isInSale = true;
        afterSale = false;
      } else {
        // After sale period
        stage = "afterSale";
        countDown = -1;
        isInSale = false;
        afterSale = true;
      }

      return {
        stage,
        countDown,
        isInSale,
        afterSale,
      };
    },

    /**
     * Handles countdown end event
     * Updates stage and afterSale flag when countdown completes
     */
    handleCountEnd() {
      // Update stage based on current time
      const currentDate = dayjs();
      const saleDate = dayjs(SALE_DATE);
      const saleEndDate = dayjs(SALE_DATE_END);

      const stageData = this.calculateStageData(
        currentDate,
        saleDate,
        saleEndDate,
      );

      this.stage = stageData.stage;
      this.afterSale = stageData.afterSale;
      this.isInSale = stageData.isInSale;
      this.countDown = stageData.countDown;

      // alert("Countdown ended!");
    },

    /**
     * Handles color change event from ProductSection
     * @param {Object} color - Selected color data
     */
    handleColorChanged(color) {
      console.log("Color changed:", color);
    },

    /**
     * Handles add to cart event from ProductSection
     * @param {Object} productData - Product data with selected color
     */
    handleAddToCart(productData) {
      console.log("Add to cart:", productData);
    },

    /**
     * Handles tab change event from TabSelector
     * @param {String} tabName - Selected tab name ('vertuo' or 'original')
     */
    handleTabChanged(tabName) {
      console.log("Tab changed to (landing):", tabName);
      this.activeTab = tabName;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/scss/include/_commonInclude.scss";

.content .app {
  .landing {
    // Landing page specific styles
  }
}

.crossSelling-section {
  margin-top: rem(-93);
  z-index: 2;
  position: relative;
}

.floating--but {
  position: fixed;
  z-index: 300;
  bottom: rem(100);
  right: 0;
  width: rem(170);
  display: block;
  cursor: pointer;

  animation: scale-pulse 2s ease-in-out infinite;
  transform-origin: 100% 0;

  background: linear-gradient(90deg, #d1ab54 0%, #ecd09e 37.02%, #d1ab54 100%);

  border-top-left-radius: 1000px;
  border-bottom-left-radius: 1000px;
  display: flex;
  align-items: center;
  justify-content: center;
  // padding: rem(18);
  padding: 0.85rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1) translateX(-0.5rem);
  }

  @include media-breakpoint-down(md) {
    padding: rem(8) rem(12);
    width: rem(98);
  }

  &__background {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
  }

  &__bg-image {
    position: absolute;
    height: 335.71%;
    left: -52.36%;
    max-width: none;
    top: -117.86%;
    width: 204.73%;
  }

  &__overlay {
    position: absolute;
    background: rgba(245, 225, 212, 0.5);
    inset: 0;
  }

  &__content {
    position: absolute;
    bottom: 100%;
    left: 0%;

    .mega {
      width: 80%;
      margin-bottom: -18%;

      @include media-breakpoint-down(md) {
        margin-bottom: -20%;
        margin-left: -11%;
      }
    }
  }

  &__capsule-container {
    position: absolute;
    width: rem(143);
    height: rem(115);
    bottom: 100%;
    margin-bottom: -10px;
    margin-left: -16px;
  }

  &__frame {
    position: absolute;
    height: rem(79);
    left: rem(42);
    top: rem(36);
    width: rem(60);
    z-index: 2;
  }

  &__frame-image {
    position: absolute;
    height: 100%;
    left: -24.93%;
    max-width: none;
    top: 0;
    width: 149.26%;
  }

  &__capsule {
    position: absolute;
    left: rem(8);
    top: rem(58);
    width: rem(58);
    height: rem(46);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  &__capsule-image {
    width: rem(58);
    height: rem(46);
    transform: rotate(330.674deg);
    filter: drop-shadow(0px rem(4) rem(4) rgba(0, 0, 0, 0.25));
  }

  &__text-container {
    position: relative;
    width: rem(85);
    height: rem(35);
  }

  &__text {
    color: #563111;
    font-weight: bold;
    font-size: rem(20);
    line-height: 1.3;
    text-align: center;
    text-transform: uppercase;
    width: rem(85);
    margin: 0;
    white-space: nowrap;

    @include media-breakpoint-down(md) {
      font-size: rem(14);
    }
  }
}
</style>
